import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Container,
  Paper,
  Fade
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { sendSubUserOTP, verifySubUserOTP, setSubUserPassword } from '../../api/authApi';
import { useAuth } from '../../context/AuthContext';
import EmailVerifyDialog from './EmailVerifyDialog';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import AuthBlueprintHero, { AuthBlueprintPanel } from './AuthBlueprintHero';

const themeColor = 'rgb(57, 170, 252)';

// Input field styles with Roboto font
const inputFieldStyles = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Roboto, sans-serif',
    backgroundColor: '#fff',
    borderRadius: '14px',
    transition: 'box-shadow 0.25s ease',
    '& fieldset': {
      borderColor: '#e3e8ef',
    },
    '&:hover fieldset': {
      borderColor: themeColor,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 4px ${themeColor}1f`,
    },
    '&.Mui-focused fieldset': {
      borderColor: themeColor,
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px', // 16px prevents iOS from auto-zooming on focus
    padding: '15px 14px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    '&.Mui-focused': {
      color: themeColor,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Roboto, sans-serif',
  },
};

// Shared gradient primary button (tactile press + glow)
const primaryButtonSx = {
  py: 1.6,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  mb: 2,
  borderRadius: '14px',
  color: '#fff',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
  boxShadow: `0 10px 24px ${themeColor}59`,
  transition: 'transform 0.15s ease, box-shadow 0.25s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${themeColor} 0%, #1565C0 100%)`,
    boxShadow: `0 12px 30px ${themeColor}80`,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.75,
  },
};

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
});

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Must contain at least one special character'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match')
});

const SubUserFirstLogin = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login: setAuthUser } = useAuth();

  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Password
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showOTPDialog, setShowOTPDialog] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  // Step 1: Submit Email
  const handleEmailSubmit = async (values, { setFieldError }) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await sendSubUserOTP({ email: values.email });

      if (response.success) {
        setEmail(values.email);
        setShowOTPDialog(true);
      }
    } catch (err) {
      setError(err.message || 'Error sending OTP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Verify OTP
  const handleOTPVerify = async (otp) => {
    try {
      const response = await verifySubUserOTP({ email, otp });

      if (response.success) {
        setShowOTPDialog(false);
        setStep(3); // Move to password step
      }
    } catch (err) {
      throw new Error(err.message || 'Invalid OTP. Please try again.');
    }
  };

  // Step 3: Set Password
  const handlePasswordSubmit = async (values, { setFieldError }) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await setSubUserPassword({
        email,
        password: values.password,
        confirmPassword: values.confirmPassword
      });

      if (response.success) {
        // Store user data in AuthContext
        setAuthUser(response.user);
        navigate('/');
      }
    } catch (err) {
      if (err.errors && Array.isArray(err.errors)) {
        setError(err.errors.join(', '));
      } else {
        setError(err.message || 'Error setting password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await sendSubUserOTP({ email });
    } catch (err) {
      throw new Error(err.message || 'Error resending OTP');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        pt: { xs: 'max(16px, env(safe-area-inset-top))', sm: 3 },
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
        background: {
          xs: 'linear-gradient(160deg, #081b3a 0%, #0d2b5c 55%, #123a75 100%)',
          sm: 'linear-gradient(160deg, #0d47a1 0%, #1565C0 32%, #42A5F5 68%, #90CAF9 100%)',
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-15%',
          left: '-20%',
          width: '70vw',
          height: '70vw',
          maxWidth: '520px',
          maxHeight: '520px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(66,165,245,0) 70%)',
          filter: 'blur(20px)',
          animation: 'floatA 14s ease-in-out infinite',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-15%',
          width: '80vw',
          height: '80vw',
          maxWidth: '560px',
          maxHeight: '560px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(13,71,161,0.45) 0%, rgba(13,71,161,0) 70%)',
          filter: 'blur(20px)',
          animation: 'floatB 18s ease-in-out infinite',
        },
        '@keyframes floatA': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(30px, 40px) scale(1.08)' },
        },
        '@keyframes floatB': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-40px, -30px) scale(1.12)' },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: { xs: 1, sm: 4 }, px: { xs: 0, sm: 2 }, maxWidth: { md: '960px' } }}>
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: { xs: '28px', sm: '24px' },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 24px 70px rgba(8, 47, 121, 0.35), 0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '42%', flexShrink: 0 }}>
                <AuthBlueprintPanel tagline="First-Time Sub-User Login" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
            {isMobile && <AuthBlueprintHero tagline="First-Time Sub-User Login" />}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 5, sm: 6 }, pb: 4, textAlign: 'center', background: `linear-gradient(135deg, ${themeColor}08 0%, transparent 100%)` }}>

              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 1,
                  fontSize: { xs: '24px', sm: '28px' },
                }}
              >
                {step === 1 && 'Sub-User First-Time Login'}
                {step === 3 && 'Set Your Password'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  color: '#666',
                  fontSize: '15px',
                }}
              >
                {step === 1 && 'Enter your email to get started'}
                {step === 3 && 'Create a password for your account'}
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pb: { xs: 5, sm: 6 } }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                  {error}
                </Alert>
              )}

              {/* Step 1: Email Input */}
              {step === 1 && (
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={emailSchema}
                  onSubmit={handleEmailSubmit}
                >
                  {({ errors, touched }) => (
                    <Form>
                      <Field name="email">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            sx={inputFieldStyles}
                          />
                        )}
                      </Field>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={primaryButtonSx}
                      >
                        {submitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Send OTP'
                        )}
                      </Button>

                      <Box
                        sx={{
                          textAlign: 'center',
                          mt: 3,
                          pt: 3,
                          borderTop: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#666', fontSize: '14px' }}>
                          Remember your credentials?{' '}
                          <Link
                            component={RouterLink}
                            to="/login"
                            sx={{
                              fontFamily: 'Roboto, sans-serif',
                              color: themeColor,
                              fontWeight: 700,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Back to Login
                          </Link>
                        </Typography>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}

              {/* Step 3: Password Input */}
              {step === 3 && (
                <Formik
                  initialValues={{ password: '', confirmPassword: '' }}
                  validationSchema={passwordSchema}
                  onSubmit={handlePasswordSubmit}
                >
                  {({ errors, touched, values }) => (
                    <Form>
                      <Field name="password">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            sx={inputFieldStyles}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={togglePassword} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>

                      {values.password && (
                        <Box sx={{ mb: 2 }}>
                          <PasswordStrengthIndicator password={values.password} />
                        </Box>
                      )}

                      <Field name="confirmPassword">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Confirm Password"
                            type={showPassword ? 'text' : 'password'}
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            sx={inputFieldStyles}
                          />
                        )}
                      </Field>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={primaryButtonSx}
                      >
                        {submitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Set Password & Login'
                        )}
                      </Button>
                    </Form>
                  )}
                </Formik>
              )}
            </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>

        {/* Footer Text */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            textAlign: 'center',
            mt: 3,
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '13px',
          }}
        >
          © {new Date().getFullYear()} DigiBooks. All rights reserved.
        </Typography>
      </Container>

      {/* Email OTP Verification Dialog */}
      <EmailVerifyDialog
        open={showOTPDialog}
        onClose={() => setShowOTPDialog(false)}
        email={email}
        onVerify={handleOTPVerify}
        onResend={handleResendOTP}
      />
    </Box>
  );
};

export default SubUserFirstLogin;
